import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Html5Qrcode, Html5QrcodeScanType } from 'html5-qrcode';

const BarcodeScanner = ({ onScan, onClose }) => {
  const [isScanning, setIsScanning] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState('requesting'); // 'requesting', 'granted', 'denied', 'error'
  const [errorMessage, setErrorMessage] = useState('');
  const scannerRef = useRef(null);
  const html5QrcodeRef = useRef(null);

  // Helper pour détecter iOS
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  // Fonction utilitaire pour calculer la taille du qrbox dynamiquement
  const getQrbox = () => {
    const vw = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
    const vh = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0);
    // Prendre 70% de la largeur de l'écran, mais max 350px
    const size = Math.min(Math.floor(vw * 0.7), 350);
    return { width: size, height: size };
  };

  // Définir toutes les fonctions AVANT de les utiliser
  const stopScanner = useCallback(() => {
    const instance = html5QrcodeRef.current;
    if (!instance) return;
    // Libérer la référence tout de suite pour qu'un appel concurrent (ex: démontage
    // juste après un scan réussi) ne redéclenche pas un second stop() sur la même
    // instance pendant que le premier est encore en cours (bloque la caméra sur iOS)
    html5QrcodeRef.current = null;
    if (instance.isScanning) {
      instance.stop().catch((error) => {
        console.error('Erreur lors de l\'arrêt du scanner:', error);
      });
    }
  }, []);

  const onScanSuccess = useCallback((decodedText) => {
    console.log('Code-barres détecté:', decodedText);
    stopScanner();
    onScan(decodedText);
  }, [onScan, stopScanner]);

  const onScanFailure = useCallback((error) => {
    // Erreur de scan - on continue à scanner
    console.log('Erreur de scan:', error);
  }, []);

  const startScanner = useCallback(async () => {
    try {
      // Vérifier que l'élément existe
      const readerElement = document.getElementById("reader");
      if (!readerElement) {
        console.error('Élément reader non trouvé, nouvelle tentative dans 200ms...');
        setTimeout(() => {
          if (permissionStatus === 'granted' && isScanning) {
            startScanner();
          }
        }, 200);
        return;
      }

      // Forcer le style du reader et de la vidéo
      readerElement.style.width = '100vw';
      readerElement.style.height = '100vh';
      readerElement.style.maxWidth = '100vw';
      readerElement.style.maxHeight = '100vh';
      readerElement.style.position = 'relative';
      readerElement.style.overflow = 'hidden';

      // Créer une nouvelle instance de Html5Qrcode
      html5QrcodeRef.current = new Html5Qrcode("reader");

      // Utiliser la méthode correcte pour obtenir les caméras
      const cameras = await Html5Qrcode.getCameras();
      
      if (cameras && cameras.length) {
        // Essayer de trouver la caméra arrière
        let selectedCamera = cameras[0]; // Par défaut, première caméra
        const backCamera = cameras.find(camera => 
          camera.label.toLowerCase().includes('back') || 
          camera.label.toLowerCase().includes('arrière') ||
          camera.label.toLowerCase().includes('rear')
        );
        if (backCamera) {
          selectedCamera = backCamera;
        }

        // Démarrer le scan avec la caméra sélectionnée
        await html5QrcodeRef.current.start(
          { deviceId: selectedCamera.id },
          {
            fps: 10,
            qrbox: getQrbox(),
            aspectRatio: 1.0,
            disableFlip: false,
            experimentalFeatures: {
              useBarCodeDetectorIfSupported: true
            }
          },
          onScanSuccess,
          onScanFailure
        );

        // Forcer le style de la vidéo/canvas généré par html5-qrcode
        setTimeout(() => {
          const video = readerElement.querySelector('video');
          if (video) {
            video.style.width = '100vw';
            video.style.height = '100vh';
            video.style.objectFit = 'cover';
            video.style.maxWidth = '100vw';
            video.style.maxHeight = '100vh';
          }
          const canvas = readerElement.querySelector('canvas');
          if (canvas) {
            canvas.style.width = '100vw';
            canvas.style.height = '100vh';
            canvas.style.objectFit = 'cover';
            canvas.style.maxWidth = '100vw';
            canvas.style.maxHeight = '100vh';
          }
        }, 500);

      } else {
        throw new Error('Aucune caméra disponible');
      }
    } catch (error) {
      console.error('Erreur lors du démarrage du scanner:', error);
      setPermissionStatus('error');
      setErrorMessage('Erreur lors du démarrage du scanner: ' + error.message);
    }
  }, [permissionStatus, isScanning, onScanSuccess, onScanFailure]);

  const checkCameraPermission = useCallback(async () => {
    try {
      // Vérifier si l'API MediaDevices est supportée
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setPermissionStatus('error');
        setErrorMessage('Votre navigateur ne supporte pas l\'accès à la caméra');
        return;
      }

      // Demander la permission d'accès à la caméra arrière
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: "environment", // Caméra arrière
          width: { ideal: 1280 },
          height: { ideal: 720 }
        } 
      });
      
      // Permission accordée
      setPermissionStatus('granted');
      setIsScanning(true);
      
      // Arrêter le stream temporairement
      stream.getTracks().forEach(track => track.stop());
      
    } catch (error) {
      console.error('Erreur de permission caméra:', error);
      
      if (error.name === 'NotAllowedError') {
        setPermissionStatus('denied');
        setErrorMessage('Permission d\'accès à la caméra refusée. Veuillez autoriser l\'accès dans les paramètres de votre navigateur.');
      } else if (error.name === 'NotFoundError') {
        setPermissionStatus('error');
        setErrorMessage('Aucune caméra trouvée sur votre appareil.');
      } else if (error.name === 'OverconstrainedError') {
        // Si la caméra arrière n'est pas disponible, essayer avec une contrainte moins stricte
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ 
            video: { 
              facingMode: "user", // Caméra avant comme fallback
              width: { ideal: 1280 },
              height: { ideal: 720 }
            } 
          });
          
          setPermissionStatus('granted');
          setIsScanning(true);
          stream.getTracks().forEach(track => track.stop());
          return;
        } catch (fallbackError) {
          setPermissionStatus('error');
          setErrorMessage('Impossible d\'accéder à la caméra. Erreur: ' + fallbackError.message);
        }
      } else {
        setPermissionStatus('error');
        setErrorMessage('Erreur lors de l\'accès à la caméra: ' + error.message);
      }
    }
  }, []);

  const handleStopScan = useCallback(() => {
    stopScanner();
    onClose();
  }, [stopScanner, onClose]);

  const handleRetryPermission = useCallback(() => {
    setPermissionStatus('requesting');
    setErrorMessage('');
    checkCameraPermission();
  }, [checkCameraPermission]);

  useEffect(() => {
    // Vérifier les permissions de caméra avant de démarrer
    checkCameraPermission();
    
    return () => {
      stopScanner();
    };
  }, [checkCameraPermission, stopScanner]);

  // Nouveau useEffect pour démarrer le scanner quand l'élément est prêt
  useEffect(() => {
    if (permissionStatus === 'granted' && isScanning) {
      // Attendre que l'élément DOM soit rendu et visible
      const timer = setTimeout(() => {
        startScanner();
      }, 300); // Délai plus long pour s'assurer que le DOM est prêt
      
      return () => clearTimeout(timer);
    }
  }, [permissionStatus, isScanning, startScanner]);

  // Afficher l'état de chargement
  if (permissionStatus === 'requesting') {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Scanner un code-barres</h3>
            <button
              onClick={handleStopScan}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-gray-600">Demande d'accès à la caméra...</p>
            <p className="text-sm text-gray-500 mt-2">Veuillez autoriser l'accès quand votre navigateur le demande</p>
          </div>
        </div>
      </div>
    );
  }

  // Afficher l'erreur de permission
  if (permissionStatus === 'denied' || permissionStatus === 'error') {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Scanner un code-barres</h3>
            <button
              onClick={handleStopScan}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <div className="text-center py-6">
            <div className="text-red-500 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <p className="text-gray-800 mb-4">{errorMessage}</p>
            
            {permissionStatus === 'denied' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4 text-left">
                <h4 className="font-semibold text-blue-800 mb-2">Comment autoriser l'accès :</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Cliquez sur l'icône de caméra dans la barre d'adresse</li>
                  <li>• Sélectionnez "Autoriser"</li>
                  <li>• Ou allez dans les paramètres de votre navigateur</li>
                </ul>
              </div>
            )}
            
            <div className="flex space-x-3">
              <button
                onClick={handleRetryPermission}
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg flex-1"
              >
                Réessayer
              </button>
              <button
                onClick={handleStopScan}
                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg flex-1"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Scanner actif
  return (
    <div className="fixed inset-0 bg-black z-50">
      <div className="relative w-full h-full">
        {/* Header avec bouton fermer */}
        <div className="absolute top-4 left-4 right-4 z-10 flex justify-between items-center">
          <h3 className="text-white text-lg font-semibold drop-shadow-lg">Scanner un code-barres</h3>
          <button
            onClick={handleStopScan}
            className="bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-2 rounded-full transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Zone de scan */}
        <div className="w-full h-full">
          <div id="reader" className="w-full h-full"></div>
        </div>

        {/* Instructions en bas */}
        <div className="absolute bottom-8 left-4 right-4 z-10 text-center">
          <div className="bg-black bg-opacity-50 text-white px-4 py-2 rounded-lg">
            <p className="text-sm">Pointez votre caméra vers le code-barres</p>
            {isIOS && (
              <p className="text-xs text-yellow-300 mt-2">Astuce iPhone : approchez bien le code-barres, la détection peut être plus lente sur Safari/iOS.</p>
            )}
          </div>
        </div>

        {/* Cadre de scan (optionnel) */}
        <div className="absolute inset-0 pointer-events-none z-5">
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 border-2 border-white border-dashed rounded-lg opacity-50"
            style={{ width: getQrbox().width, height: getQrbox().height }}
          ></div>
        </div>
      </div>
    </div>
  );
};

export default BarcodeScanner; 